import { useMemo, useState, useEffect, useCallback } from 'react';
import { paths } from '@/routes/paths';
import { useRouter } from '@/routes/hooks';
import { useAuthContext } from '../hooks';

const loginPaths: Record<string, string> = {
  jwt: paths.auth.jwt.login,
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: Props) {
  const router = useRouter();
  const { authenticated, method } = useAuthContext();
  const [checked, setChecked] = useState(false);

  // Memoize login paths to avoid recreating on every render
  const loginPathsMemo = useMemo(() => loginPaths, []);

  // Memoize current pathname to avoid unnecessary recalculations
  const currentPathname = useMemo(() => window.location.pathname, []);

  // Memoize search params to avoid recreating on every check
  const searchParams = useMemo(
    () =>
      new URLSearchParams({
        returnTo: currentPathname,
      }).toString(),
    [currentPathname]
  );

  // Memoize login path to avoid recalculating
  const loginPath = useMemo(() => loginPathsMemo[method], [loginPathsMemo, method]);

  // Memoize full login href to avoid recreating
  const loginHref = useMemo(() => `${loginPath}?${searchParams}`, [loginPath, searchParams]);

  const check = useCallback(() => {
    if (!authenticated) {
      router.replace(loginHref);
    } else {
      setChecked(true);
    }
  }, [authenticated, router, loginHref]);

  useEffect(() => {
    check();
  }, [check]);

  // Memoize loading state to avoid unnecessary re-renders
  const isLoading = useMemo(() => !checked, [checked]);

  if (isLoading) {
    return null;
  }

  return <>{children}</>;
}
